import React, { useRef } from "react";
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from "classnames";
import withStyles from "isomorphic-style-loader/withStyles";
import { Container } from "react-bootstrap";
import Loader from '../../components/Common/Loader/Loader';
import Link from '../../components/Link/Link';
import NotFound from "../not-found/NotFound";

import PriceDetails from "../../components/BookingDetail/PriceDetails/PriceDetails"
import OrderDetails from "../../components/BookingDetail/OrderDetails/OrderDetails";
import ConfirmBookingBanner from "../../components/ConfirmBookingBanner/ConfirmBookingBanner"
import ViewBookingSkeleton from "../../components/Skeleton/ViewBooking/ViewBookingSkeleton/ViewBookingSkeleton";

import messages from "../../locale/messages";
import { encode } from "../../helpers/queryEncryption";
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import getOrder from "./getOrder.graphql";
import { AngleLeftArrowIcon } from "../../components/Common/Assets/SVGIcon/CommonSvgIcon";
import bt from '../../components/commonStyle.css';
import s from './ViewBooking.css'


const ViewBooking = () => {
    const { data, loading } = useQueryResponse({
        query: getOrder,
        variables: {
            promoId: null
        },
        ssr: false,
        fetchPolicy: 'network-only'
    })

    let initialValues = {
        validPromoCode: data?.getOrder?.result?.promoId,
        promoId: data?.getOrder?.result?.promoId,
    }

    let categoryId = data?.getOrder?.result?.categoryDetails?.id || ''
    // HARDCODED guard against the "flow jumps to my bookings" bug. The Choose-for-me (nearestProvider)
    // and Preferred (preferredProvider) connecting/waiting screens CONSUME the cart, so getOrder.result
    // goes null mid-flow. The previous guard depended on transient redux modal flags, which have a
    // one-render gap during the bookingForm -> connecting-modal transition (two separate dispatches in
    // an async .then, not batched in React 17) — the redirect leaked through that gap and yanked the
    // client off the connecting screen. Instead, latch whether an order was EVER present in this mount:
    // every legitimate exit (accept / on-board -> /tracking, or Cancel) navigates itself, so once an
    // order has been seen we NEVER auto-redirect. We only redirect on a genuinely stale/empty load.
    const hadOrderRef = useRef(false);
    if (data?.getOrder?.result != null) hadOrderRef.current = true;

    // Show the skeleton only WHILE loading. A cold/empty visit (no active cart, hadOrderRef = false)
    // now renders an empty-cart state instead of auto-redirecting to /bookings/upcoming — a stray
    // Back/refresh onto /order/:id no longer bounces the user away. A null order MID-FLOW
    // (hadOrderRef = true) still falls through to the normal render so ConfirmBookingBanner keeps the
    // Choose-for-me / Preferred connecting modal alive.
    const isEmptyCart = !loading && data?.getOrder?.result == null && !hadOrderRef.current;

    if (loading) {
        return <div className={'subPageContainer'}>
            <Container>
                <ViewBookingSkeleton />
            </Container>
        </div>
    } else if (isEmptyCart) {
        return <div className={'subPageContainer'}>
            <Container>
                <div className={s.emptyCartContainer}>
                    <div className={cx(bt.commonViewBookingHeading, bt.space2)}>
                        <FormattedMessage {...messages.noActiveBooking} />
                    </div>
                    <div className={cx(bt.commonSize14, bt.fontWeight400, bt.space3)}>
                        <FormattedMessage {...messages.noActiveBookingDesc} />
                    </div>
                    <Link to={'/'} className={cx(bt.btnPrimary, bt.cursorPointer)}>
                        <FormattedMessage {...messages.startABooking} />
                    </Link>
                </div>
            </Container>
        </div>
    } else {
        return (
            <div className={'subPageContainer'}>
                <Container>
                    <>
                        <div className={cx(bt.space3, s.topLinkContainer)}>
                            <Link to={`/category?type=${encodeURIComponent(encode(categoryId?.toString()))}`} className={cx(bt.customLink, 'customLinkRtl')} >
                                <AngleLeftArrowIcon />
                                <FormattedMessage {...messages.goBackText} />
                            </Link>
                        </div>
                        <div className={bt.commonViewBookingContainer}>
                            <div className={bt.commonViewBookingLsBox}>
                                <OrderDetails loading={loading} data={data?.getOrder} reviewPage={true} isOrderPage={true} />
                            </div>

                            <div className={bt.commonStickyRsContainer}>
                                <PriceDetails loading={loading} data={data?.getOrder} isOrderPage={true} promoCodeValues={initialValues} />
                            </div>
                        </div>

                    </>
                    <ConfirmBookingBanner loading={loading} data={data?.getOrder} />
                </Container>
            </div>
        )
    }
}
export default injectIntl(withStyles(bt, s)(ViewBooking))